import React, { useState, useEffect, useRef } from 'react';
import {
  Megaphone, Plus, Edit, Trash2, Eye, EyeOff, Save, X,
  Image, Link, Users, Crown, Shield, UserCheck, Search,
  ChevronDown, Check, AlertCircle, Bell, Layout, Square, Palette, Globe
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Preset colors for quick selection
const PRESET_COLORS = [
  { name: 'Mint', value: '#10b981' },
  { name: 'Blue', value: '#3b82f6' },
  { name: 'Purple', value: '#8b5cf6' },
  { name: 'Red', value: '#ef4444' },
  { name: 'Orange', value: '#f97316' },
  { name: 'Yellow', value: '#eab308' },
  { name: 'Pink', value: '#ec4899' },
  { name: 'Teal', value: '#14b8a6' },
  { name: 'Indigo', value: '#6366f1' },
  { name: 'Gray', value: '#6b7280' },
  { name: 'Black', value: '#1f2937' },
  { name: 'White', value: '#ffffff' }
];

// Common emojis for icon selection
const COMMON_EMOJIS = ['📢', '🎉', '⭐', '🔥', '💡', '🎁', '❤️', '✨', '🚀', '📌', '⚡', '🌟', '💫', '🔔', '📣', '🎊'];

const AdminAnnouncements = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  
  // User selection for specific targeting
  const [allUsers, setAllUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const dropdownRef = useRef(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    displayType: 'banner',
    targetAudience: 'all',
    specificUserIds: [],
    imageUrl: '',
    linkUrl: '',
    linkText: '',
    isActive: true,
    priority: 0
  });
  
  // Banner styling state
  const [bannerStyle, setBannerStyle] = useState({
    backgroundColor: '#10b981',
    textColor: '#ffffff',
    gradientColor: '#059669',
    styleType: 'solid',
    icon: '',
    boldTitle: true
  });
  
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(null); // 'bg', 'text', 'gradient'
  
  const { toast } = useToast();

  useEffect(() => {
    fetchAnnouncements();
    fetchAllUsers();
  }, [page]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter users based on search query
  useEffect(() => {
    if (userSearchQuery.trim() === '') {
      setFilteredUsers(allUsers);
    } else {
      const query = userSearchQuery.toLowerCase();
      const filtered = allUsers.filter(user =>
        user.firstName?.toLowerCase().includes(query) ||
        user.lastName?.toLowerCase().includes(query) ||
        user.email?.toLowerCase().includes(query)
      );
      setFilteredUsers(filtered);
    }
  }, [userSearchQuery, allUsers]);

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/announcements/admin/list?page=${page}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnnouncements(response.data.announcements || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching announcements:', error);
      toast({
        title: "Error",
        description: "Failed to load announcements",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/contact/admin/all-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllUsers(response.data.users || []);
      setFilteredUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      displayType: 'banner',
      targetAudience: 'all',
      specificUserIds: [],
      imageUrl: '',
      linkUrl: '',
      linkText: '',
      isActive: true,
      priority: 0
    });
    setBannerStyle({
      backgroundColor: '#10b981',
      textColor: '#ffffff',
      gradientColor: '#059669',
      styleType: 'solid',
      icon: '',
      boldTitle: true
    });
    setSelectedUsers([]);
    setEditingId(null);
    setShowForm(false);
    setShowColorPicker(null);
  };

  const handleEdit = (announcement) => {
    setFormData({
      title: announcement.title || '',
      content: announcement.content || '',
      displayType: announcement.displayType || 'banner',
      targetAudience: announcement.targetAudience || 'all',
      specificUserIds: announcement.specificUserIds || [],
      imageUrl: announcement.imageUrl || '',
      linkUrl: announcement.linkUrl || '',
      linkText: announcement.linkText || '',
      isActive: announcement.isActive !== false,
      priority: announcement.priority || 0
    });
    
    // Load banner style if exists
    if (announcement.bannerStyle) {
      setBannerStyle({
        backgroundColor: announcement.bannerStyle.backgroundColor || '#10b981',
        textColor: announcement.bannerStyle.textColor || '#ffffff',
        gradientColor: announcement.bannerStyle.gradientColor || '#059669',
        styleType: announcement.bannerStyle.styleType || 'solid',
        icon: announcement.bannerStyle.icon || '',
        boldTitle: announcement.bannerStyle.boldTitle !== false
      });
    } else {
      setBannerStyle({
        backgroundColor: '#10b981',
        textColor: '#ffffff',
        gradientColor: '#059669',
        styleType: 'solid',
        icon: '',
        boldTitle: true
      });
    }
    
    // Set selected users from specificUserIds
    if (announcement.specificUserIds?.length > 0) {
      const selected = allUsers.filter(u => announcement.specificUserIds.includes(u.id));
      setSelectedUsers(selected);
    } else {
      setSelectedUsers([]);
    }
    
    setEditingId(announcement.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.content.trim()) {
      toast({
        title: "Error",
        description: "Title and content are required",
        variant: "destructive"
      });
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      const payload = {
        ...formData,
        specificUserIds: selectedUsers.map(u => u.id),
        bannerStyle: formData.displayType === 'banner' ? bannerStyle : null
      };
      
      if (editingId) {
        // Update existing
        await axios.put(
          `${API_URL}/api/announcements/admin/${editingId}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast({
          title: "Success",
          description: "Announcement updated successfully"
        });
      } else {
        // Create new
        await axios.post(
          `${API_URL}/api/announcements/create`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast({
          title: "Success",
          description: "Announcement created successfully"
        });
      }
      
      resetForm();
      fetchAnnouncements();
    } catch (error) {
      console.error('Error saving announcement:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to save announcement",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (announcement) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API_URL}/api/announcements/admin/${announcement.id}/toggle`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({
        title: "Success",
        description: `Announcement ${announcement.isActive ? 'deactivated' : 'activated'}`
      });
      fetchAnnouncements();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to toggle status",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async (announcementId) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_URL}/api/announcements/admin/${announcementId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({
        title: "Success",
        description: "Announcement deleted successfully"
      });
      fetchAnnouncements();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete announcement",
        variant: "destructive"
      });
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    try {
      setUploadingImage(true);
      const token = localStorage.getItem('token');
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await axios.post(
        `${API_URL}/api/announcements/upload-image`,
        formDataUpload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      setFormData(prev => ({ ...prev, imageUrl: response.data.imageUrl }));
      toast({
        title: "Success",
        description: "Image uploaded successfully"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to upload image",
        variant: "destructive"
      });
    } finally {
      setUploadingImage(false);
    }
  };

  const handleUserSelect = (user) => {
    const isSelected = selectedUsers.some(u => u.id === user.id);
    if (isSelected) {
      setSelectedUsers(selectedUsers.filter(u => u.id !== user.id));
    } else {
      setSelectedUsers([...selectedUsers, user]);
    }
  };

  const getDisplayTypeIcon = (type) => {
    switch (type) {
      case 'banner': return <Layout className="w-4 h-4" />;
      case 'popup': return <AlertCircle className="w-4 h-4" />;
      case 'card': return <Square className="w-4 h-4" />;
      default: return <Bell className="w-4 h-4" />;
    }
  };

  const getTargetAudienceIcon = (target) => {
    switch (target) {
      case 'public': return <Globe className="w-4 h-4" />;
      case 'all': return <Users className="w-4 h-4" />;
      case 'premium': return <Crown className="w-4 h-4" />;
      case 'verified': return <Shield className="w-4 h-4" />;
      case 'specific': return <UserCheck className="w-4 h-4" />;
      default: return <Users className="w-4 h-4" />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get banner preview style
  const getBannerPreviewStyle = () => {
    const style = {};
    
    if (bannerStyle.styleType === 'gradient') {
      style.background = `linear-gradient(to right, ${bannerStyle.backgroundColor}, ${bannerStyle.gradientColor})`;
    } else if (bannerStyle.styleType === 'bordered') {
      style.background = 'transparent';
      style.border = `2px solid ${bannerStyle.backgroundColor}`;
    } else {
      style.background = bannerStyle.backgroundColor;
    }
    
    style.color = bannerStyle.textColor;
    
    return style;
  };

  if (loading && announcements.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mint-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Announcements</h1>
          <p className="text-gray-600">Create and manage announcements for users</p>
        </div>
        <Button
          onClick={() => setShowForm(true)}
          className="bg-mint-500 hover:bg-mint-600"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Announcement
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Announcements</p>
                <p className="text-2xl font-bold">{total}</p>
              </div>
              <Megaphone className="w-8 h-8 text-mint-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active</p>
                <p className="text-2xl font-bold text-green-600">
                  {announcements.filter(a => a.isActive).length}
                </p>
              </div>
              <Eye className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Inactive</p>
                <p className="text-2xl font-bold text-gray-400">
                  {announcements.filter(a => !a.isActive).length}
                </p>
              </div>
              <EyeOff className="w-8 h-8 text-gray-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Create/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>{editingId ? 'Edit Announcement' : 'Create Announcement'}</span>
                <button onClick={resetForm} className="text-gray-500 hover:text-gray-700">
                  <X className="w-5 h-5" />
                </button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-transparent"
                    placeholder="Announcement title"
                    required
                  />
                </div>

                {/* Content */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Content *
                  </label>
                  <textarea
                    value={formData.content}
                    onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-transparent"
                    placeholder="Announcement message..."
                    rows={3}
                    required
                  />
                </div>

                {/* Display Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Display Type
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { value: 'banner', label: 'Top Banner', icon: Layout },
                      { value: 'popup', label: 'Popup Modal', icon: AlertCircle },
                      { value: 'card', label: 'Dashboard Card', icon: Square }
                    ].map(option => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, displayType: option.value }))}
                        className={`p-3 border rounded-lg flex flex-col items-center gap-1 transition-colors ${
                          formData.displayType === option.value
                            ? 'border-mint-500 bg-mint-50 text-mint-700'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <option.icon className="w-5 h-5" />
                        <span className="text-xs">{option.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Banner Style Options (only for banner type) */}
                {formData.displayType === 'banner' && (
                  <div className="p-4 border border-gray-200 rounded-lg bg-gray-50 space-y-4">
                    <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                      <Palette className="w-4 h-4" />
                      Banner Style Options
                    </div>

                    {/* Style Type */}
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Style</label>
                      <div className="grid grid-cols-4 gap-2">
                        {[
                          { value: 'solid', label: 'Solid' },
                          { value: 'gradient', label: 'Gradient' },
                          { value: 'bordered', label: 'Bordered' }
                        ].map(option => (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => setBannerStyle(prev => ({ ...prev, styleType: option.value }))}
                            className={`p-2 text-xs border rounded transition-colors ${
                              bannerStyle.styleType === option.value
                                ? 'border-mint-500 bg-mint-50 text-mint-700'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Background Color */}
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Background Color</label>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {PRESET_COLORS.map(color => (
                          <button
                            key={color.value}
                            type="button"
                            onClick={() => setBannerStyle(prev => ({ ...prev, backgroundColor: color.value }))}
                            className={`w-6 h-6 rounded-full border-2 transition-all ${
                              bannerStyle.backgroundColor === color.value ? 'border-gray-800 scale-110' : 'border-transparent'
                            }`}
                            style={{ backgroundColor: color.value }}
                            title={color.name}
                          />
                        ))}
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={bannerStyle.backgroundColor}
                          onChange={(e) => setBannerStyle(prev => ({ ...prev, backgroundColor: e.target.value }))}
                          className="w-8 h-8 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={bannerStyle.backgroundColor}
                          onChange={(e) => setBannerStyle(prev => ({ ...prev, backgroundColor: e.target.value }))}
                          className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded"
                          placeholder="#10b981"
                        />
                      </div>
                    </div>

                    {/* Gradient Color (only for gradient style) */}
                    {bannerStyle.styleType === 'gradient' && (
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Gradient End Color</label>
                        <div className="flex flex-wrap gap-2 mb-2">
                          {PRESET_COLORS.map(color => (
                            <button
                              key={color.value}
                              type="button"
                              onClick={() => setBannerStyle(prev => ({ ...prev, gradientColor: color.value }))}
                              className={`w-6 h-6 rounded-full border-2 transition-all ${
                                bannerStyle.gradientColor === color.value ? 'border-gray-800 scale-110' : 'border-transparent'
                              }`}
                              style={{ backgroundColor: color.value }}
                              title={color.name}
                            />
                          ))}
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="color"
                            value={bannerStyle.gradientColor}
                            onChange={(e) => setBannerStyle(prev => ({ ...prev, gradientColor: e.target.value }))}
                            className="w-8 h-8 rounded cursor-pointer"
                          />
                          <input
                            type="text"
                            value={bannerStyle.gradientColor}
                            onChange={(e) => setBannerStyle(prev => ({ ...prev, gradientColor: e.target.value }))}
                            className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded"
                            placeholder="#059669"
                          />
                        </div>
                      </div>
                    )}

                    {/* Text Color */}
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Text Color</label>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {PRESET_COLORS.map(color => (
                          <button
                            key={color.value}
                            type="button"
                            onClick={() => setBannerStyle(prev => ({ ...prev, textColor: color.value }))}
                            className={`w-6 h-6 rounded-full border-2 transition-all ${
                              bannerStyle.textColor === color.value ? 'border-gray-800 scale-110' : 'border-transparent'
                            }`}
                            style={{ backgroundColor: color.value }}
                            title={color.name}
                          />
                        ))}
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={bannerStyle.textColor}
                          onChange={(e) => setBannerStyle(prev => ({ ...prev, textColor: e.target.value }))}
                          className="w-8 h-8 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={bannerStyle.textColor}
                          onChange={(e) => setBannerStyle(prev => ({ ...prev, textColor: e.target.value }))}
                          className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded"
                          placeholder="#ffffff"
                        />
                      </div>
                    </div>

                    {/* Icon/Emoji */}
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Icon (Emoji)</label>
                      <div className="flex flex-wrap gap-2 mb-2">
                        <button
                          type="button"
                          onClick={() => setBannerStyle(prev => ({ ...prev, icon: '' }))}
                          className={`w-8 h-8 rounded border flex items-center justify-center text-xs ${
                            !bannerStyle.icon ? 'border-mint-500 bg-mint-50' : 'border-gray-200'
                          }`}
                        >
                          None
                        </button>
                        {COMMON_EMOJIS.map(emoji => (
                          <button
                            key={emoji}
                            type="button"
                            onClick={() => setBannerStyle(prev => ({ ...prev, icon: emoji }))}
                            className={`w-8 h-8 rounded border flex items-center justify-center text-lg ${
                              bannerStyle.icon === emoji ? 'border-mint-500 bg-mint-50' : 'border-gray-200'
                            }`}
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                      <input
                        type="text"
                        value={bannerStyle.icon}
                        onChange={(e) => setBannerStyle(prev => ({ ...prev, icon: e.target.value }))}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        placeholder="Enter custom emoji..."
                        maxLength={4}
                      />
                    </div>

                    {/* Bold Title */}
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={bannerStyle.boldTitle}
                        onChange={(e) => setBannerStyle(prev => ({ ...prev, boldTitle: e.target.checked }))}
                        className="w-4 h-4 text-mint-500 rounded focus:ring-mint-500"
                        id="boldTitle"
                      />
                      <label htmlFor="boldTitle" className="text-sm text-gray-700">
                        Bold title text
                      </label>
                    </div>

                    {/* Live Preview */}
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Preview</label>
                      <div
                        className="p-3 rounded-lg text-center transition-all"
                        style={getBannerPreviewStyle()}
                      >
                        <span className={bannerStyle.boldTitle ? 'font-semibold' : ''}>
                          {bannerStyle.icon && <span className="mr-2">{bannerStyle.icon}</span>}
                          {formData.title || 'Your announcement title'}
                        </span>
                        {formData.content && (
                          <span className="ml-2 opacity-90">{formData.content}</span>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Target Audience */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Target Audience
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                    {[
                      { value: 'public', label: 'Public', icon: Globe, desc: 'Before login' },
                      { value: 'all', label: 'All Users', icon: Users, desc: 'After login' },
                      { value: 'premium', label: 'Premium', icon: Crown, desc: 'After login' },
                      { value: 'verified', label: 'Verified', icon: Shield, desc: 'After login' },
                      { value: 'specific', label: 'Specific', icon: UserCheck, desc: 'After login' }
                    ].map(option => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, targetAudience: option.value }))}
                        className={`p-2 border rounded-lg flex flex-col items-center gap-1 transition-colors ${
                          formData.targetAudience === option.value
                            ? 'border-mint-500 bg-mint-50 text-mint-700'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <option.icon className="w-4 h-4" />
                        <span className="text-xs font-medium">{option.label}</span>
                        <span className="text-[10px] text-gray-500">{option.desc}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* User Selection (for specific targeting) */}
                {formData.targetAudience === 'specific' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Select Users
                    </label>
                    
                    {/* Selected Users */}
                    {selectedUsers.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-2">
                        {selectedUsers.map(user => (
                          <Badge
                            key={user.id}
                            className="bg-mint-100 text-mint-800 flex items-center gap-1 pr-1"
                          >
                            {user.firstName} {user.lastName}
                            <button
                              type="button"
                              onClick={() => setSelectedUsers(selectedUsers.filter(u => u.id !== user.id))}
                              className="ml-1 hover:bg-mint-200 rounded-full p-0.5"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    )}
                    
                    {/* Search Dropdown */}
                    <div className="relative" ref={dropdownRef}>
                      <div
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg flex items-center gap-2 cursor-pointer"
                        onClick={() => setShowUserDropdown(!showUserDropdown)}
                      >
                        <Search className="w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          value={userSearchQuery}
                          onChange={(e) => {
                            setUserSearchQuery(e.target.value);
                            setShowUserDropdown(true);
                          }}
                          onFocus={() => setShowUserDropdown(true)}
                          placeholder="Search users..."
                          className="flex-1 outline-none bg-transparent"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showUserDropdown ? 'rotate-180' : ''}`} />
                      </div>
                      
                      {showUserDropdown && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-auto">
                          {filteredUsers.length === 0 ? (
                            <div className="p-3 text-center text-gray-500 text-sm">No users found</div>
                          ) : (
                            filteredUsers.slice(0, 50).map(user => {
                              const isSelected = selectedUsers.some(u => u.id === user.id);
                              return (
                                <div
                                  key={user.id}
                                  onClick={() => handleUserSelect(user)}
                                  className={`px-3 py-2 cursor-pointer flex items-center gap-2 hover:bg-gray-50 ${
                                    isSelected ? 'bg-mint-50' : ''
                                  }`}
                                >
                                  <div className="flex-1">
                                    <p className="text-sm font-medium">{user.firstName} {user.lastName}</p>
                                    <p className="text-xs text-gray-500">{user.email}</p>
                                  </div>
                                  {isSelected && <Check className="w-4 h-4 text-mint-500" />}
                                </div>
                              );
                            })
                          )}
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{selectedUsers.length} user(s) selected</p>
                  </div>
                )}

                {/* Image Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Image (Optional)
                  </label>
                  <div className="flex items-center gap-4">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      id="image-upload"
                    />
                    <label
                      htmlFor="image-upload"
                      className="px-4 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 flex items-center gap-2"
                    >
                      <Image className="w-4 h-4" />
                      {uploadingImage ? 'Uploading...' : 'Upload Image'}
                    </label>
                    {formData.imageUrl && (
                      <div className="flex items-center gap-2">
                        <img src={formData.imageUrl} alt="Preview" className="w-10 h-10 object-cover rounded" />
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, imageUrl: '' }))}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Link */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Link URL (Optional)
                    </label>
                    <input
                      type="url"
                      value={formData.linkUrl}
                      onChange={(e) => setFormData(prev => ({ ...prev, linkUrl: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-transparent"
                      placeholder="https://..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Link Text
                    </label>
                    <input
                      type="text"
                      value={formData.linkText}
                      onChange={(e) => setFormData(prev => ({ ...prev, linkText: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-transparent"
                      placeholder="Click here"
                    />
                  </div>
                </div>

                {/* Priority */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Priority (Higher = Shows First)
                  </label>
                  <input
                    type="number"
                    value={formData.priority}
                    onChange={(e) => setFormData(prev => ({ ...prev, priority: parseInt(e.target.value) || 0 }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-transparent"
                    min="0"
                    max="100"
                  />
                </div>

                {/* Active Status */}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                    className="w-4 h-4 text-mint-500 rounded focus:ring-mint-500"
                    id="isActive"
                  />
                  <label htmlFor="isActive" className="text-sm text-gray-700">
                    Active (visible to users)
                  </label>
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-4">
                  <Button
                    type="submit"
                    disabled={saving}
                    className="flex-1 bg-mint-500 hover:bg-mint-600"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? 'Saving...' : (editingId ? 'Update' : 'Create')}
                  </Button>
                  <Button
                    type="button"
                    onClick={resetForm}
                    variant="outline"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Announcements List */}
      <Card>
        <CardHeader>
          <CardTitle>All Announcements</CardTitle>
        </CardHeader>
        <CardContent>
          {announcements.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Megaphone className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <p>No announcements yet</p>
              <Button
                onClick={() => setShowForm(true)}
                className="mt-4 bg-mint-500 hover:bg-mint-600"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create First Announcement
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {announcements.map((announcement) => (
                <div
                  key={announcement.id}
                  className={`p-4 border rounded-lg ${
                    announcement.isActive ? 'border-green-200 bg-green-50/50' : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold text-gray-900">{announcement.title}</h3>
                        <Badge className={announcement.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
                          {announcement.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-600 mb-3 line-clamp-2">{announcement.content}</p>
                      <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          {getDisplayTypeIcon(announcement.displayType)}
                          {announcement.displayType}
                        </span>
                        <span className="flex items-center gap-1">
                          {getTargetAudienceIcon(announcement.targetAudience)}
                          {announcement.targetAudience === 'specific'
                            ? `${announcement.specificUserIds?.length || 0} users`
                            : announcement.targetAudience}
                        </span>
                        <span>Priority: {announcement.priority}</span>
                        <span>Created: {formatDate(announcement.createdAt)}</span>
                      </div>
                      {announcement.imageUrl && (
                        <img src={announcement.imageUrl} alt="" className="mt-2 h-16 rounded object-cover" />
                      )}
                      {/* Show banner style preview */}
                      {announcement.displayType === 'banner' && announcement.bannerStyle && (
                        <div className="mt-2">
                          <span className="text-xs text-gray-500">Style: </span>
                          <span
                            className="inline-block px-2 py-0.5 rounded text-xs"
                            style={{
                              background: announcement.bannerStyle.styleType === 'gradient'
                                ? `linear-gradient(to right, ${announcement.bannerStyle.backgroundColor}, ${announcement.bannerStyle.gradientColor})`
                                : announcement.bannerStyle.styleType === 'bordered'
                                ? 'transparent'
                                : announcement.bannerStyle.backgroundColor,
                              color: announcement.bannerStyle.textColor,
                              border: announcement.bannerStyle.styleType === 'bordered'
                                ? `1px solid ${announcement.bannerStyle.backgroundColor}`
                                : 'none'
                            }}
                          >
                            {announcement.bannerStyle.icon && `${announcement.bannerStyle.icon} `}
                            {announcement.bannerStyle.styleType}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={() => handleToggleStatus(announcement)}
                        variant="ghost"
                        size="sm"
                        title={announcement.isActive ? 'Deactivate' : 'Activate'}
                      >
                        {announcement.isActive ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                      <Button
                        onClick={() => handleEdit(announcement)}
                        variant="ghost"
                        size="sm"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => handleDelete(announcement.id)}
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Pagination */}
          {total > 20 && (
            <div className="flex justify-center gap-2 mt-6">
              <Button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                variant="outline"
                size="sm"
              >
                Previous
              </Button>
              <span className="px-4 py-2 text-sm">Page {page}</span>
              <Button
                onClick={() => setPage(page + 1)}
                disabled={page * 20 >= total}
                variant="outline"
                size="sm"
              >
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminAnnouncements;
